import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { TriangleRight } from "lucide-react";

import TableComponent from "./TableComponent";
import Pagination from "../view/common/pagination/Pagination";
import DetailsPopUp from "../view/DetailsPopup"; 
import { updateFilters, resetFilters } from "../redux/slices/filterSlice";
import { RootState } from "../redux/slices/store";
import { getData } from "../api/ApiServices";
import "../styles/table-search.css";
import "../styles/ItemModels.css";
import "../styles/modal.css";
import "../styles/common/sub-menu.css";
import "../styles/common/spinner.css";
import "../styles/common/toplayout.css";
import "../styles/common/btn.css";
import "../styles/common/icon-style.css";
import "../styles/common/table-heading-icons.css";
import "../styles/StockSummaryStyles.css";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const StockSummary: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const filters = useSelector((state: RootState) => state.filters);

  const [stockSummaryData, setStockSummaryData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [dateValidationError, setDateValidationError] = useState<string>("");
  const [activeFilter, setActiveFilter] = useState<boolean>(false);

  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(null);

  const [initializeStockSummary, isInitializeStockSummary] = useState<boolean>(false);

  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [selectedPartId, setSelectedPartId] = useState<string>("");

  const columns = [
    {
      key: "partId",
      label: "Part No",
      nameKey: "internalPartNo",
      render: (value: any) =>
        value && typeof value === "object" ? value.internalPartNo || "N/A" : "N/A",
      sortable: true
    },
    {
      key: "partId",
      label: "Part Name",
      nameKey: "partName",
      render: (value: any) =>
        value && typeof value === "object" ? value.partName || "N/A" : "N/A",
      sortable: true
    },
    {
      key: "partId",
      label: "Model",
      nameKey: "model.name",
      render: (value: any) =>
        value && typeof value === "object" ? value?.model?.name || "N/A" : "N/A",
      sortable: true
    },
    {
      key: "partId",
      label: "Category",
      nameKey: "category.name",
      render: (value: any) =>
        value && typeof value === "object" ? value?.category?.name || "N/A" : "N/A",
      sortable: true
    },
    {
      key: "partId",
      label: "Uom",
      nameKey: "uom.name",
      render: (value: any) =>
        value && typeof value === "object" ? value?.uom?.name || "N/A" : "N/A"
    },
    {
      key: "openingStock",
      label: "Opening Stock",
      sortable: true,
      isRightAlign: true
    },
    {
      key: "inwardQty",
      label: "Inward Qty",
      sortable: true,
      isRightAlign: true
    },
    {
      key: "outwardQty",
      label: "Outward Qty",
      sortable: true,
      isRightAlign: true
    },
    {
      key: "closingStock",
      label: "Closing Stock",
      sortable: true,
      isRightAlign: true,
      render: (value: number, rowData: any) => (
        <div 
          className="stock-details-cell clickable"
          onClick={(e) => {
            e.stopPropagation();
            if (rowData.partId && rowData.partId._id) {
              handleClosingStockClick(rowData.partId._id);
            }
          }}
          style={{ cursor: "pointer" }}
          title="Click to view details"
        >
          <span>{value}</span>
          {rowData.partId && rowData.partId._id && (
            <TriangleRight size={12} color="#808080" style={{ fill: '#808080' }} />
          )}
        </div>
      )
    }
  ];

  const today = new Date().toISOString().split("T")[0];

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  };

  const dateFormatDDMMYYYY = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const [StartDate, setStartDate] = useState<Date | null>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  });
  const [EndDate, setEndDate] = useState<Date | null>(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  });

  const handleClosingStockClick = (partId: string) => {
    setSelectedPartId(partId);
    setIsPopupOpen(true);
  };

  const applyDateFilter = () => {
    if (StartDate == null) {
      setDateValidationError("Start date is required.");
      setActiveFilter(false);
      return;
    }

    if (EndDate == null) {
      setDateValidationError("End date is required.");
      setActiveFilter(false);
      return;
    }
    if (StartDate && EndDate && StartDate > EndDate) {
      setDateValidationError("Start date cannot be after end date.");
      setActiveFilter(false);
      return;
    }

    setDateValidationError("");

    setAppliedStartDate(StartDate);
    setAppliedEndDate(EndDate);

    setActiveFilter(true);

    setCurrentPage(1);

    fetchstockSummaryData();
  };

  const fetchstockSummaryData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const queryParams: any = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        filter: filters,
      };

      if (StartDate != null) {
        queryParams.startdate = StartDate.toISOString().split("T")[0];
      }
      if (EndDate != null) {
        queryParams.enddate = EndDate.toISOString().split("T")[0];
      }

      const response: any = await getData<{
        data: any[];
        total: number;
        pages: number;
      }>("/stocksummary", queryParams);
      console.log("API Response:", response);

      const formattedData = response.data.data.map((item: any) => {
        let enhancedItem = {
          ...item,
          grnDate: dateFormatDDMMYYYY(item.grnDate),
        };

        if (item.partId && item.partId.part) {
          enhancedItem.partDetails = JSON.stringify({
            internalPartNo: item.partId.part.internalPartNo,
            externalPartNo: item.partId.part.externalPartNo,
            partName: item.partId.part.partName,
            hsnCode: item.partId.part.hsnCode,
            packingQty: item.partId.part.packingQty,
            status: item.partId.part.status,
          });
        }

        if (item.poId) {
          enhancedItem.poDetails = JSON.stringify({
            poNo: item.poId.poNo || "N/A",
            poDate: item.poId.poDate
              ? new Date(item.poId.poDate).toLocaleDateString("en-CA")
              : "N/A",
          });
        }

        if (item.poId && item.poId.poNo) {
          enhancedItem.poNo = item.poId.poNo;
        }

        return enhancedItem;
      });

      if (response && response.data) {
        setStockSummaryData(formattedData);
        setTotalItems(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 0);
      } else {
        setStockSummaryData([]);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error("Error fetching GRN data:", err);
      setError("Failed to load GRN report data. Please try again later.");
      setStockSummaryData([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = (
    sortByField: string,
    sortOrderValue: "asc" | "desc"
  ) => {
    setSortBy(sortByField);
    setSortOrder(sortOrderValue);
  };

  useEffect(() => {
    dispatch(resetFilters());
    isInitializeStockSummary(true);
  },[dispatch])

  useEffect(() => {
    console.log("filters: ", filters);
    if (initializeStockSummary) {
      fetchstockSummaryData();
    }
  }, [initializeStockSummary,currentPage, itemsPerPage, sortBy, sortOrder, filters]);

  if (isLoading && stockSummaryData.length === 0) {
    return (
      <div className="item-models-container">
        <div className="spinner"></div>
      </div>
    );
  }

  function onChangeHandlerStartDate(value: Date | null) {
    setStartDate(value);
  }

  function onChangeHandlerEndDate(value: Date | null) {
    setEndDate(value);
  }

  const CustomInput = React.forwardRef(({ value, onClick }: any, ref: any) => (
    <input
      readOnly
      className="date-input"
      onClick={onClick}
      value={value}
      ref={ref}
      style={{ width: "130px" }}
    />
  ));

  return (
    <div className="item-models-container">
      <div className="item-models-header">
        <div className="border">
          <div className="start-end-layout">
            <div className="breadcrumb-nav">
              <span>Stocks</span>
              <span>Stock Summary</span>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="date-filters-container">
          <div className="date-filters">
            <div className="date-filter-label">Filtering By Dates</div>
            <div className="date-filter-group">
              <label className="date-filter-label" htmlFor="start-date">
                Start Date:
              </label>
              <DatePicker
                selected={StartDate}
                onChange={onChangeHandlerStartDate}
                dateFormat="dd '- 'MM '- 'yyyy"
                placeholderText="Select Start Date"
                customInput={<CustomInput />}
                maxDate={new Date() ?? undefined}
              />
            </div>

            <div className="date-filter-group">
              <label className="date-filter-label" htmlFor="end-date">
                End Date:
              </label>
              <DatePicker
                selected={EndDate}
                onChange={onChangeHandlerEndDate}
                dateFormat="dd '- 'MM '- 'yyyy"
                placeholderText="Select End Date"
                customInput={<CustomInput />}
                maxDate={new Date() ?? undefined}
              />
            </div>

            <button className="filter-button" onClick={applyDateFilter}>
              Apply Filter
            </button>
          </div>

          {dateValidationError && (
            <div className="error-filter-message">{dateValidationError}</div>
          )}

          {activeFilter && (
            <p className="active-filter-message">
              {appliedStartDate && appliedEndDate
                ? `Showing data from ${dateFormatDDMMYYYY(
                  appliedStartDate.toISOString().split("T")[0]
                )} to ${dateFormatDDMMYYYY(
                  appliedEndDate.toISOString().split("T")[0]
                )}`
                : ""}
            </p>
          )}
        </div>

        {error && <div className="stock-error-message">{error}</div>}

        <div className="box-container">
          <TableComponent
            columns={columns}
            data={stockSummaryData}
            setFilter={(filters: Record<string, any>) => {
              Object.entries(filters).forEach(([key, value]) => {
                dispatch(updateFilters({ key, value: String(value) }));
              });
            }}
            setSorting={handleSortChange}
            defaultSortConfig={{ key: sortBy, direction: sortOrder }}
            excludeFields={["status","grnDate"]}
            context="default"
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            totalItems={totalItems}
          />
        </div>
      </div>
      <DetailsPopUp
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        partId={selectedPartId}
        startDate={StartDate ? StartDate.toISOString().split("T")[0] : undefined}
        endDate={EndDate ? EndDate.toISOString().split("T")[0] : undefined}
        isStockSummary={true}
      />
    </div>
  );
};

export default StockSummary;